import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { CoutryStateCityService } from 'src/app/common/country-state-city.service';
import { MaterialModule } from 'src/app/material.module';
import { HospitalService } from '../hospital.service';
import { ToastrService } from 'ngx-toastr';
import { UserService } from '../../user/user.service';
import { ValidationService } from 'src/app/common/validation/validation.service';
import { Router } from '@angular/router';
import { AllowOnlyDigitsDirective } from 'src/app/common/directive/allow-only-digits.directive';
import { LoginService } from 'src/app/login.service';
import { LogoutService } from 'src/app/auth/logout.service';
import { RolesOfUsers } from 'src/app/common/types/common';
import { MaskInputDirective } from 'src/app/common/directive/mask-input.directive';

@Component({
  selector: 'app-hospital',
  standalone: true,
  imports: [
    MaterialModule,
    CommonModule,
    TablerIconsModule,
    AllowOnlyDigitsDirective,
    MaskInputDirective,
    TranslateModule,
    ReactiveFormsModule
  ],
  templateUrl: './hospital-detail.component.html',
  styleUrl: './hospital-detail.component.scss'
})
export class HospitalDetailComponent implements OnInit {
  hospitalForm: FormGroup;
  UserForm: FormGroup;
  Countries: any[] = [];
  states: any = [];
  cities: any = [];
  hidePassword = true;
  selectedImage: File | null = null;
  showAddPhotoOverlay = false;
  defaultAvatarPath = 'assets/images/profile/user-1.jpg';
  imageUrl: string | ArrayBuffer | null = null;
  base64Image: string;
  hideData = false;
  get userRole(): string[] {
    if (this.hospitalForm?.get('isPharmacyEnabled')?.value) {
      return RolesOfUsers;
    }

    return RolesOfUsers.filter(role => role !== 'Pharmacist');
  }

  constructor(
    private _formBuilder: FormBuilder,
    private _hospitalService: HospitalService,
    private _coutrystatecityService: CoutryStateCityService,
    private dialogRef: MatDialogRef<HospitalDetailComponent>,
    private toastr: ToastrService,
    private translate: TranslateService,
    private _userService: UserService,
    private _router: Router,
    private _loginService: LoginService,
    private _logoutService: LogoutService,
    private cdr: ChangeDetectorRef
  ) {
    this.Countries = this._coutrystatecityService.getCountries();
  }
  ngOnInit(): void {

    this.hospitalForm = this._formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      panNumber: ['', [Validators.required, Validators.maxLength(10), Validators.minLength(10), ValidationService.panNumberValidator]],
      registrationNumber: ['', Validators.required],
      phone: ['', [Validators.pattern(/^[6-9]\d{9}$/), Validators.minLength(10), Validators.maxLength(10)]],
      mobile: ['', [Validators.required, Validators.pattern(/^[6-9]\d{9}$/), Validators.minLength(10), Validators.maxLength(10)]],
      country: [''],
      state: [''],
      city: [''],
      postalCode: [''],
      address: ['', Validators.required],
      isIPD: [false],
      isPharmacyEnabled: [false],
      isActive: [true]
    });

    this.UserForm = this._formBuilder.group({
      firstname: ['', [Validators.required, Validators.maxLength(20)]],
      lastname: ['', [Validators.required, Validators.maxLength(20)]],
      email: ['', [Validators.required, ValidationService.emailValidator]],
      username: ['', [Validators.required, Validators.maxLength(50)]],
      password: ['', [Validators.required, Validators.minLength(10)]],
      role: [[]],
    });

    this.Countries = this._coutrystatecityService.getCountries();

    this.hospitalForm.get('country')?.valueChanges.subscribe((selectedCountry) => {
      this.onCountryChange(selectedCountry);
    });

    // Listen for changes in the state dropdown
    this.hospitalForm.get('state')?.valueChanges.subscribe((selectedState) => {
      this.onStateChange(selectedState);
    });
  }

  onCountryChange(country: string) {
    const selectedCountry = this.Countries.find(a => a.name === country);
    const countryIsoCode = selectedCountry ? selectedCountry.isoCode : '';
    this.states = this._coutrystatecityService.getStatesByCountry(countryIsoCode);
    this.hospitalForm.get('state')?.setValue('');
    this.cities = [];
  }

  onStateChange(state: string) {
    const country = this.hospitalForm.get('country')?.value;
    const selectedState = this.states.find((s: { name: string; }) => s.name === state);
    const stateIsoCode = selectedState ? selectedState.isoCode : '';
    const selectedCountry = this.Countries.find(country => country.name === this.hospitalForm.get('country')?.value);
    const countryIsoCode = selectedCountry ? selectedCountry.isoCode : '';
    this.cities = this._coutrystatecityService.getCitiesByState(countryIsoCode, stateIsoCode);
    this.hospitalForm.get('city')?.setValue('');
  }

  onSaveHospital() {
    const formData = new FormData();

    Object.entries(this.hospitalForm.getRawValue()).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });

    Object.entries(this.UserForm.getRawValue()).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });
    const roles = this.UserForm.get('role')?.value;
    roles.forEach((role: string) => {
      formData.append('roles', role);
    });

    if (this.selectedImage) {
      formData.append("logo", this.selectedImage);
    }

    this._hospitalService.createHospitalWithUser(formData).subscribe({
      next: (res) => {
        if (res && res.isSuccess) {
          this.toastr.success(this.translate.instant('COMMON.HOSPITAL_USER_SAVE'));
          this._logoutService.logout();
          this.dialogRef.close(true);
        } else {
          this.toastr.error(res?.message || 'Failed to create hospital and user');
        }
      },
      error: (err) => {
        console.error(err);
        this.toastr.error('Unexpected error occurred');
      }
    });
  }

  onCloseDialog(): void {
    this.dialogRef.close();

  }

  togglePasswordVisibility() {
    this.hidePassword = !this.hidePassword;
  }

  get profileImagePathSrc(): string {
    if (this.imageUrl) {
      return this.imageUrl as string;
    }
    return this.defaultAvatarPath;
  }

  onFileChanged(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      const reader = new FileReader();
      this.selectedImage = file;
      reader.onload = () => {
        this.imageUrl = reader.result;
        this.base64Image = reader.result as string;
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(file);
    }
    this.showAddPhotoOverlay = false;
  }
}
